import type { FetchArgs } from "@reduxjs/toolkit/query/react";
import { createApi } from "@reduxjs/toolkit/query/react";
import { format, subDays } from "date-fns";

import type { ExtraType } from "../../app/configureStore";
import { getArtesianQueryParams } from "../../helpers";
import type { ArkPagedQueryParameters, ListResponseArtesian } from "../../lib/apiTypes";
import { ArkBaseQuery } from "../../lib/authentication/arkBaseQuery";
import type { SelectedRow } from "../../store/selectedRowsSlice";
import type {
  AclMeResponse,
  AclResponse,
  AddNewGroupRequest,
  PermissionPathRequest,
  UserGroup,
  UserGroupsResponse,
} from "../admin/types";
import type { ApiKeyCreatePayload, ApiKeyType } from "../apiKey/apiKeyTypes";
import type { GetAuctionOffersRequest, GetAuctionOffersResponse } from "../auctionOffer/types";
import type { ClassificationPayload } from "../classifications/types";
import type { EnrichMetadataPayload } from "../enrichMetaData/types";
import type { AddAlertRequest, ExpectationAlertResponse, PastCheckResponse } from "../expectationAlert/types";

import type {
  ArtesianDataSeriesType,
  ArtesianPreviewDataType,
  AuctionOfferResponse,
  CustomFilterType,
  DataSeriesResponse,
  MarketDataType,
  MarketDataTypeQuery,
  Operator,
  TimeTransformType,
  Unit,
  UomConversionResponse,
} from "./artesianDataTypes";

const todaysDate = format(new Date(), "yyyy-MM-dd");
const yesterdaysDate = format(subDays(new Date(), 1), "yyyy-MM-dd");
const lastWeekDate = format(subDays(new Date(), 7), "yyyy-MM-dd");

const getDataPreviewUrl = (row: ArtesianDataSeriesType) => {
  switch (row.Type) {
    case "MarketAssessment":
      return `/query/v1.0/mas/${lastWeekDate}/${todaysDate}?id=${row.MarketDataId}&p=M%2B1&p=GY%2B1&p=Cal%2B1&p=Q%2B1&p=S%2B1&p=D%2B1&p=WkEnd%2B1`;
    case "ActualTimeSerie":
      return `/query/v1.0/ts/${row.OriginalGranularity}/${yesterdaysDate}/${todaysDate}?id=${row.MarketDataId}`;
    case "BidAsk":
      return `/query/v1.0/ba/${lastWeekDate}/${todaysDate}?id=${row.MarketDataId}&p=M%2B1&p=GY%2B1&p=Cal%2B1&p=Q%2B1&p=S%2B1&p=D%2B1&p=WkEnd%2B1`;
    case "Auction":
      return `/query/v1.0/auction/${yesterdaysDate}/${todaysDate}?id=${row.MarketDataId}`;
    default:
      return `/query/v1.0/vts/MUV/${row.OriginalGranularity}/${yesterdaysDate}/${todaysDate}?id=${row.MarketDataId}`;
  }
};

export const artesianApiSlice = createApi({
  reducerPath: "artesianApi",
  baseQuery: (args, api, extra) => ArkBaseQuery(args as FetchArgs, api, extra as ExtraType, "artesianApi"),
  tagTypes: ["DataSeries", "CustomFilters", "ExpectationAlerts", "UserGroups", "Acl", "ApiKeys", "TimeTransforms"],
  endpoints: builder => ({
    getDataSeries: builder.query<DataSeriesResponse, ArkPagedQueryParameters>({
      query: params => {
        let sorting =
          params.sorting && params.sorting.length > 0 ? params.sorting : [{ id: "MarketDataId", desc: false }];
        if (sorting.some(sort => sort.id === "relevance")) {
          sorting = [];
        }
        const filters = getArtesianQueryParams({ ...params, sorting });
        return {
          url: `/v2.1/marketdata/searchfacet`,
          params: filters,
        };
      },
      transformResponse: (response: DataSeriesResponse) => {
        const typeFacet = response.Facets?.find(f => f.FacetName === "Type");
        const filteredTypes = typeFacet?.Values?.map(v => v.Value) ?? [];
        return { ...response, filteredTypes };
      },
      providesTags: ["DataSeries"],
    }),
    getDataPreview: builder.query<ArtesianPreviewDataType[], ArtesianDataSeriesType>({
      query: row => {
        const url = getDataPreviewUrl(row);
        return {
          url: url,
        };
      },
    }),

    getcustomFilters: builder.query<ListResponseArtesian<CustomFilterType>, null>({
      query: () => {
        return {
          url: `/v2.1/filter?page=1&pageSize=500`,
        };
      },
      providesTags: ["CustomFilters"],
    }),
    postCustomFilter: builder.mutation<CustomFilterType, CustomFilterType>({
      query: filter => {
        const url = filter.Id === -1 ? `/v2.1/filter` : `/v2.1/filter/${filter.Id}`;
        return {
          url: url,
          method: filter.Id === -1 ? "POST" : "PUT",
          body: filter,
        };
      },
      invalidatesTags: ["CustomFilters"],
    }),
    deleteCustomFilter: builder.mutation<void, number>({
      query: id => {
        return {
          url: `/v2.1/filter/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["CustomFilters"],
    }),
    getExpectationAlertData: builder.query<ExpectationAlertResponse, ArkPagedQueryParameters>({
      query: params => {
        let sorting = params.sorting && params.sorting.length > 0 ? params.sorting : [];
        if (sorting.some(sort => sort.id === "relevance")) {
          sorting = [];
        }
        const filters = getArtesianQueryParams({ ...params, sorting });
        return {
          url: `/v2.1/expectationAlert`,
          params: filters,
        };
      },
      providesTags: ["ExpectationAlerts"],
    }),
    deleteExpectationAlert: builder.mutation<void, number>({
      query: id => ({
        url: `/v2.1/expectationAlert/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ExpectationAlerts"],
    }),
    addExpectationAlertDataSeries: builder.mutation<void, { alertId: number; marketDataIds: number[] }>({
      query: ({ alertId, marketDataIds }) => ({
        url: `/v2.1/expectationAlert/${alertId}/marketDataList`,
        method: "PUT",
        body: marketDataIds, // Send the array of MarketData IDs as the body
      }),
      invalidatesTags: ["ExpectationAlerts"],
    }),
    deleteExpectationAlertDataSeries: builder.mutation<void, { alertId: number; marketDataIds: number[] }>({
      query: ({ alertId, marketDataIds }) => ({
        url: `/v2.1/expectationAlert/${alertId}/marketDataList`,
        method: "DELETE",
        body: marketDataIds, // Send the array of MarketData IDs as the body
      }),
      invalidatesTags: ["ExpectationAlerts"],
    }),
    addExpectationAlert: builder.mutation<void, AddAlertRequest>({
      query: payload => ({
        url: `/v2.1/expectationAlert`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["ExpectationAlerts"],
    }),
    getPastChecks: builder.query<
      PastCheckResponse,
      { alertId: number; page: number; pageSize: number; startDate?: string; endDate?: string }
    >({
      query: ({ alertId, page, pageSize, startDate, endDate }) => ({
        url: `/v2.1/expectationAlert/${alertId}/pastChecks`,
        params: { page, pageSize, startDate, endDate }, // Include date range in query parameters
      }),
    }),
    getUserGroups: builder.query<UserGroupsResponse, void>({
      query: () => ({
        url: `/v2.1/group`,
        params: {
          page: 1,
          pageSize: 500,
        },
      }),
      providesTags: ["UserGroups"],
    }),
    editUsersInGroup: builder.mutation<void, { groupId: number; updatedGroup: UserGroup }>({
      query: ({ groupId, updatedGroup }) => ({
        url: `/v2.1/group/${groupId}`,
        method: "PUT",
        body: updatedGroup,
      }),
      invalidatesTags: ["UserGroups"],
    }),
    deleteUsersInGroup: builder.mutation<void, { groupId: number; updatedGroup: UserGroup }>({
      query: ({ groupId, updatedGroup }) => ({
        url: `/v2.1/group/${groupId}`,
        method: "PUT",
        body: updatedGroup,
      }),
      invalidatesTags: ["UserGroups"],
    }),
    addNewGroup: builder.mutation<void, AddNewGroupRequest>({
      query: newGroup => ({
        url: `/v2.1/group`,
        method: "POST",
        body: newGroup,
      }),
      invalidatesTags: ["Acl", "UserGroups"],
    }),
    deleteGroup: builder.mutation<void, { groupId: number }>({
      query: ({ groupId }) => ({
        url: `/v2.1/group/${groupId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Acl", "UserGroups"],
    }),
    getAclMeByPath: builder.query<AclMeResponse[], { principalId: number }>({
      query: ({ principalId }) => ({
        url: `/v2.1/acl/me`, // Correct endpoint
        params: {
          path: `/principal/group/${principalId}`, // Correct payload
        },
      }),
      providesTags: ["Acl"],
    }),

    getAclMeByPathString: builder.query<AclMeResponse[], { path: string }>({
      query: ({ path }) => ({
        url: `/v2.1/acl/me`,
        params: {
          path: `/${path}/`,
        },
      }),
      providesTags: ["Acl"],
    }),
    getAclByPrincipalId: builder.query<AclResponse, { principalId: string; page: number; pageSize: number }>({
      query: ({ principalId, page, pageSize }) => ({
        url: `/v2.1/acl`,
        params: {
          principalIds: principalId,
          page: page,
          pageSize: pageSize,
        },
      }),
      providesTags: ["Acl"],
    }),
    addPermissionPath: builder.mutation<void, PermissionPathRequest>({
      query: ({ path, roles }) => ({
        url: `/v2.1/acl/roles`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          Path: path,
          Roles: roles.map(role => ({
            Principal: {
              PrincipalType: role.principalType,
              PrincipalId: role.principalId,
            },
            Role: role.role,
          })),
        },
      }),
      invalidatesTags: ["Acl"], // Invalidate Acl cache if needed
    }),
    deletePermissionPath: builder.mutation<void, PermissionPathRequest>({
      query: ({ path, roles }) => ({
        url: `/v2.1/acl/roles`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          Path: path,
          Roles: roles.map(role => ({
            Principal: {
              PrincipalType: role.principalType,
              PrincipalId: role.principalId,
            },
            Role: role.role,
          })),
        },
      }),
      invalidatesTags: ["Acl"], // Invalidate cache if necessary
    }),
    getApikey: builder.query<ListResponseArtesian<ApiKeyType>, ArkPagedQueryParameters>({
      query: filters => {
        const payload = getArtesianQueryParams(filters);
        return {
          url: `/v2.1/apikey/entity`,
          method: "GET",
          params: payload,
        };
      },
      providesTags: ["ApiKeys"],
    }),
    postApiKey: builder.mutation<ApiKeyType, ApiKeyCreatePayload>({
      query: payload => {
        return {
          url: `/v2.1/apikey/entity`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ["ApiKeys"],
    }),
    DeleteApiKey: builder.mutation<ApiKeyType, number>({
      query: id => {
        return {
          url: `/v2.1/apikey/entity/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["ApiKeys"],
    }),
    updateMarketData: builder.mutation<SelectedRow[], EnrichMetadataPayload>({
      query: payload => ({
        url: `/v2.1/marketdata/operations`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }),
    }),
    getTimeTransform: builder.query<ListResponseArtesian<TimeTransformType>, { userDefined?: boolean }>({
      query: ({ userDefined }) => ({
        url: `/v2.1/timeTransform/entity`,
        params: {
          page: 1,
          pageSize: 2000,
          ...(userDefined !== undefined && { userDefined }),
        },
      }),
      providesTags: ["TimeTransforms"],
    }),
    editTimeTransform: builder.mutation<TimeTransformType, Partial<TimeTransformType>>({
      query: timeTransform => ({
        url: `/v2.1/timeTransform/entity/${timeTransform.ID}`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: timeTransform,
      }),
      invalidatesTags: ["TimeTransforms"],
    }),
    addTimeTransform: builder.mutation<TimeTransformType, Partial<TimeTransformType>>({
      query: timeTransform => ({
        url: `/v2.1/timeTransform/entity`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: timeTransform,
      }),
      invalidatesTags: ["TimeTransforms"],
    }),
    deleteTimeTransform: builder.mutation<void, TimeTransformType>({
      query: timeTransform => ({
        url: `/v2.1/timeTransform/entity/${timeTransform.ID}`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: timeTransform,
      }),
      invalidatesTags: ["TimeTransforms"],
    }),
    classifyRow: builder.mutation<SelectedRow[], ClassificationPayload>({
      query: payload => ({
        url: `/v2.1/marketdata/operations`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }),
      invalidatesTags: ["DataSeries"],
    }),
    getAuctionOfferPreview: builder.query<AuctionOfferResponse, string>({
      query: url => {
        return {
          url,
        };
      },
    }),
    getOperators: builder.query<{ Data: Operator[]; Count: number }, { pageIndex: number; pageSize: number }>({
      query: ({ pageIndex, pageSize }) => ({
        url: `/gmepublicoffer/v2.0/enums/operators`,
        params: {
          page: pageIndex,
          pageSize: pageSize,
        },
      }),
    }),
    getUnits: builder.query<{ Data: Unit[]; Count: number }, { pageIndex: number; pageSize: number }>({
      query: ({ pageIndex, pageSize }) => ({
        url: `/gmepublicoffer/v2.0/enums/units`,
        params: {
          page: pageIndex,
          pageSize: pageSize,
        },
      }),
    }),
    getMarketDataVersions: builder.query<ListResponseArtesian<MarketDataType>, MarketDataTypeQuery>({
      query: q => ({
        url: `/v2.1/marketData/entity/${q.marketDataId}/curves?page=1&pageSize=500&versionFrom=${q.versionFrom}&versionTo=${q.versionTo}`,
        method: "GET",
      }),
    }),
    getAuctionOffersUrl: builder.query<string, void>({
      queryFn: () => {
        // Construct the endpoint path
        const endpoint = `/gmepublicoffer/v2.0/extract/`;
        // Construct the full URL
        const fullUrl = endpoint;
        return { data: fullUrl };
      },
    }),

    getAuctionOffers: builder.query<GetAuctionOffersResponse, GetAuctionOffersRequest>({
      query: ({ date, offerType, status, pageIndex, pageSize, filters }) => {
        const url = `/gmepublicoffer/v2.0/extract/${date}/${offerType}/${status}`;
        const params: Record<string, string> = {
          page: pageIndex.toString(),
          pageSize: pageSize.toString(),
        };
        // Add filters to the query string
        if (filters) {
          if (filters.zone) params.zone = filters.zone.join(",");
          if (filters.operator) params.operator = filters.operator.join(",");
          if (filters.unit) params.unit = filters.unit.join(",");
          if (filters.market) params.market = filters.market.join(",");
          if (filters.scope) params.scope = filters.scope.join(",");
          if (filters.baType) params.baType = filters.baType.join(",");
          if (filters.unitType) params.unitType = filters.unitType.join(",");
          if (filters.generationType) params.generationType = filters.generationType.join(",");
        }

        return {
          url,
          params,
        };
      },
    }),
    checkUomConversion: builder.query<
      UomConversionResponse,
      { inputUnitsOfMeasure: string[]; targetUnitOfMeasure: string }
    >({
      query: ({ inputUnitsOfMeasure, targetUnitOfMeasure }) => {
        const filteredInputUnits = inputUnitsOfMeasure.filter(u => u != null && u.trim() !== "");
        const params = [
          ...filteredInputUnits.map(u => `inputUnitsOfMeasure=${encodeURIComponent(u)}`),
          `targetUnitOfMeasure=${encodeURIComponent(targetUnitOfMeasure)}`,
        ].join("&");
        return {
          url: `/v2.1/uom/checkconversion?${params}`,
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  useGetDataSeriesQuery,
  useGetDataPreviewQuery,
  useGetcustomFiltersQuery,
  usePostCustomFilterMutation,
  useDeleteCustomFilterMutation,
  useGetExpectationAlertDataQuery,
  useDeleteExpectationAlertMutation,
  useAddExpectationAlertDataSeriesMutation,
  useDeleteExpectationAlertDataSeriesMutation,
  useAddExpectationAlertMutation,
  useGetPastChecksQuery,
  useGetUserGroupsQuery,
  useEditUsersInGroupMutation,
  useDeleteUsersInGroupMutation,
  useDeleteGroupMutation,
  useAddNewGroupMutation,
  useGetAclByPrincipalIdQuery,
  useGetAclMeByPathStringQuery,
  useGetAclMeByPathQuery,
  useAddPermissionPathMutation,
  useDeletePermissionPathMutation,
  useGetAuctionOfferPreviewQuery,
  useGetOperatorsQuery,
  useGetUnitsQuery,
  useGetAuctionOffersUrlQuery,
  useGetAuctionOffersQuery,
  useGetApikeyQuery,
  usePostApiKeyMutation,
  useDeleteApiKeyMutation,
  useClassifyRowMutation,
  useUpdateMarketDataMutation,
  useGetTimeTransformQuery,
  useEditTimeTransformMutation,
  useAddTimeTransformMutation,
  useDeleteTimeTransformMutation,
  useGetMarketDataVersionsQuery,
  useCheckUomConversionQuery,
} = artesianApiSlice;
